import { motion } from "framer-motion";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as TakyonLogo } from "../../../assets/img/icons/TakyonLogo.svg";
import {
  getLocaleLanguage,
  setLocaleLanguage,
} from "../../../assets/Services/i18next";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import Icon from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import { SMALL_SCREEN } from "../../../assets/Utils/generic";
import { LANG } from "../../../config/Lang";
import getLink, { LINK_TYPE } from "../../../config/Links";
import { useAppDispatch } from "../../../redux/redux";
import {
  selectHeroVisibility,
  selectProfileMenuVisibility,
  setProfileMenu,
} from "../../../redux/slices/appSlice";
import {
  selectSellerPendingOffersCount,
  selectUser,
} from "../../../redux/slices/userSlice";
import Search from "../../pages/Directsearch/Search";
import openAuthModal from "../Auth/Auth";
import IconFilter from "../../../assets/icons/IconFilter";
import { openModal } from "../../../assets/Utils/modal";
import FiltersModal from "../../pages/Directsearch/FiltersModal";
import { useSearch } from "../../hooks/useSearch";

import { cn } from "../../../assets/Utils/generic";

import { ANALYTICS_SOURCE } from "../../../config/analytics";

interface Props {
  nav?: boolean;
  lang?: LANG;
  setLang?: (lang: LANG) => void;
}

function NavLang({ nav, lang, setLang }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const isHeroVisible = useSelector(selectHeroVisibility);
  const hideNavBG = isHeroVisible && pathname === "/";

  const changeLocaleLanguageSelect = (selectedLang: LANG) => {
    fireTagManagerEvent("change_language", {
      original: lang,
      final: selectedLang,
    });
    setLang?.(selectedLang);
    setLocaleLanguage(selectedLang);
    setIsOpen(false);
  };

  const initAsync = async () => {
    await getLocaleLanguage();
  };

  useEffect(() => {
    initAsync();
  }, []);

  return (
    <div className="position-relative">
      {/* <Button
        textClass="fs-body-sm p-0"
        style={{
          border: "unset !important",
          color: hideNavBG ? "#ffffff" : "#000000",
        }}
        size="sm"
        variant="blank"
        icon={isOpen ? "expand_less" : "expand_more"}
        iconColor={hideNavBG ? "#ffffff" : "#000000"}
        text={lang?.toUpperCase()}
        onClick={(e) => {
          setIsOpen(!isOpen);
          e.stopPropagation();
        }}
      /> */}
      <div
        className="d-flex align-items-center cursor-pointer"
        onClick={(e) => {
          setIsOpen(!isOpen);
          e.stopPropagation();
        }}
      >
        <Icon
          size={20}
          icon={isOpen ? "expand_less" : "expand_more"}
          type="symbol"
          color={hideNavBG ? "#ffffff" : "#000000"}
        />
        <p className={`fs-body-sm ${hideNavBG ? "text-white" : "text-black"}`}>
          {lang?.toUpperCase()}
        </p>
      </div>
      {isOpen ? (
        <div
          style={{
            top: "120%",
            right: 0,
            zIndex: 99999,
          }}
          className="position-absolute p-3 gap-2 white-background d-flex flex-column align-items-end rounded shadow"
        >
          {Object.keys(LANG).map((langKey, key) => {
            const langValue = LANG[langKey as keyof typeof LANG];
            return (
              <p
                onClick={() => {
                  changeLocaleLanguageSelect(langValue);
                  setIsOpen(false);
                }}
                className="m-0 bodytext cursor-pointer text-nowrap hover-underline"
                key={key}
              >
                <>{langValue.toUpperCase()}</>
              </p>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

interface InnerNavProps {
  showActions?: boolean;
  light?: boolean;
}

export function InnerNav({ showActions = true }: InnerNavProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });
  const [lang, setLang] = useState<LANG | undefined>();
  const isHeroVisible = useSelector(selectHeroVisibility);
  const { pathname } = useLocation();
  const offersPendingCount = useSelector(selectSellerPendingOffersCount);
  const isProfileMenuOpened = useSelector(selectProfileMenuVisibility);
  const user = useSelector(selectUser);
  const dispatch = useAppDispatch();
  const hideNavBG = isHeroVisible && pathname === "/";
  const isDS = pathname === "/directsearch/search";

  const { goToSearchUrl, searchParameters } = useSearch();

  useEffect(() => {
    const initAsync = async () => {
      const l = await getLocaleLanguage();

      const langValue: LANG | undefined = l as LANG | undefined;
      setLang(langValue);
    };
    initAsync();
  }, []);

  const numberOfActiveFilters = useMemo(
    () =>
      (searchParameters.filters
        ? Object.keys(searchParameters.filters).reduce((acc, key) => {
            const typedKey = key as keyof typeof searchParameters.filters;

            if (
              [
                "certifiedOnly",
                "isLoyaltyEnabled",
                "minReviewsScore",
                "onlyHotels",
              ].includes(key)
            ) {
              if (searchParameters.filters?.[typedKey] !== undefined) {
                return acc + 1;
              }

              return acc;
            }

            if (["stars", "facilities"].includes(key)) {
              if (searchParameters.filters?.[typedKey] !== undefined) {
                return (
                  //@ts-ignore
                  acc +
                    (searchParameters.filters?.[typedKey] as Array<unknown>)
                      ?.length ?? 0
                );
              }

              return acc;
            }

            return acc;
          }, 0)
        : 0) + 0,
    [searchParameters.filters],
  );

  return (
    <div className="w100">
      <motion.div
        className="position-absolute left-0 w100 h100 border-bottom"
        style={{
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          background: "white",
          zIndex: -1,
        }}
        initial={{ top: hideNavBG ? "-100%" : 0 }}
        animate={{ top: hideNavBG ? "-100%" : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      ></motion.div>
      <div style={{ padding: isSmallScreen ? "0 18px" : "0 16px" }}>
        <div className="d-flex justify-content-between align-items-center w100 p-0 rounded my-2">
          <div className="d-flex align-items-center gap-2">
            <div
              className="cursor-pointer"
              onClick={() => {
                const wallPath = getLink(LINK_TYPE.WALL);
                if (window.location.pathname !== wallPath) {
                  navigate(wallPath);
                } else {
                  window.scrollTo(0, 0);
                }
                dispatch(setProfileMenu(false));
              }}
            >
              <TakyonLogo
                className={cn(
                  "w-20 lg:w-28",
                  hideNavBG && "text-white",
                  !hideNavBG && "text-black",
                )}
              />
            </div>
          </div>
          {isDS && !isSmallScreen && (
            <div className="flex items-center gap-2">
              <Search
                mode={isSmallScreen ? "mobile" : "desktop"}
                showFilters={false}
                isNav
                parent="nav"
              />
              <div className="relative size-[3.125rem]">
                {numberOfActiveFilters > 0 && (
                  <div className="absolute w-2 h-5 bg-[#F3F3F3] -translate-x-full top-1/2 -translate-y-1/2 before:content-[''] before:absolute before:size-2 before:bg-white before:rounded-full before:left-1/2 before:-translate-x-1/2 before:-translate-y-2/3 after:content-[''] after:absolute after:size-2 after:bg-white after:rounded-full after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:translate-y-2/3" />
                )}
                <button
                  type="button"
                  className="size-12 rounded-2xl bg-[#F3F3F3] flex items-center justify-center border-none"
                  onClick={() => {
                    openModal(
                      {
                        content: (
                          <FiltersModal
                            onFilter={({ filters }) => {
                              goToSearchUrl(
                                { filters },
                                {
                                  triggerTagManagerEvent: false,
                                  source: ANALYTICS_SOURCE.LIST,
                                },
                              );
                            }}
                            source={ANALYTICS_SOURCE.LIST}
                          />
                        ),
                        className:
                          "rounded-t-3xl self-end h-[calc(100dvh-1rem)] lg:h-auto lg:self-center lg:rounded-3xl",
                      },
                      { hideModalCloseBtn: true },
                    );
                  }}
                >
                  <IconFilter className="size-4" />
                </button>
                {numberOfActiveFilters > 0 && (
                  <div className="absolute top-0 right-0 translate-x-0.5 -translate-y-0.5 aspect-square rounded-full bg-black size-5 text-[0.625rem] font-medium text-white flex items-center justify-center text-center">
                    {numberOfActiveFilters}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="d-flex align-items-center gap-2">
            {(isSmallScreen && !isProfileMenuOpened) || !isSmallScreen ? (
              <>
                <NavLang lang={lang} setLang={setLang} />

                {showActions ? (
                  <>
                    {!pathname.includes(getLink(LINK_TYPE.PROFILE)) && (
                      <Button
                        className="btn-nav"
                        style={{
                          backgroundColor: hideNavBG
                            ? "rgba(23, 23, 23, 0.05)"
                            : "rgba(255, 255, 255, 0)",
                          height: "38px",
                          padding: isSmallScreen ? "8px 10px" : "10px 16px",
                          marginTop: "0",
                          marginBottom: "0",
                          borderRadius: "100px",
                          boxShadow: "none",
                        }}
                        onClick={() => {
                          fireTagManagerEvent("click_become_partner", {
                            source: "nav",
                          });
                          window.open("https://business.takyon.io/", "_blank");
                        }}
                        variant="transparent"
                        textClass={`fs-body-sm ${
                          hideNavBG ? "text-white" : "text-black"
                        }`}
                        text={t("wall.stay_request_btn")}
                      />
                    )}

                    <Button
                      className={`btn-nav px-1`}
                      size="sm"
                      variant="transparent"
                      textClass={`fs-body-sm ${
                        hideNavBG ? "text-white" : "text-black"
                      }`}
                      iconStyle={{
                        fill: hideNavBG ? "white" : "black",
                      }}
                      style={{
                        backgroundColor: hideNavBG
                          ? "rgba(23, 23, 23, 0.05)"
                          : "rgba(255, 255, 255, 0)",
                        borderRadius: "100px",
                        width: "38px",
                        height: "38px",
                        padding: user ? "10px 10px" : "0px",
                        marginTop: "0",
                        marginBottom: "0",
                        border: !user
                          ? "none"
                          : hideNavBG
                          ? "1.5px solid white"
                          : "1.5px solid black",
                        boxShadow: "none",
                      }}
                      onClick={() => {
                        if (user) {
                          dispatch(setProfileMenu(true));
                        }
                        if (
                          user &&
                          user.emailVerified &&
                          typeof user.marketingEmails !== "undefined" &&
                          user.firstName &&
                          user.secondName &&
                          user.phone &&
                          user.phonePrefix &&
                          user.dateOfBirth
                        ) {
                          navigate(getLink(LINK_TYPE.PROFILE_WALLET));
                        } else {
                          openAuthModal();
                        }
                      }}
                      iconSize={24}
                      icon={user ? "" : "login"}
                      notification={
                        offersPendingCount > 0 ? offersPendingCount : undefined
                      }
                      text={
                        user
                          ? String(user.firstName?.[0] ?? "").toUpperCase() +
                            String(user.secondName?.[0] ?? "").toUpperCase()
                          : ""
                      }
                    />
                  </>
                ) : null}
              </>
            ) : (
              <>
                <Icon
                  className="black-color"
                  icon="close"
                  onClick={() => dispatch(setProfileMenu(false))}
                ></Icon>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Nav() {
  return (
    <nav className="top-0 fixed w-full z-20" id="navbar">
      <InnerNav />
    </nav>
  );
}
